import { Drawer, Tabs, Box, Tab } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { ReactElement, useRef, useState } from 'react'
import { defaultTheme } from '../../../theme/index'
import { useUiStateStore } from '../../../store/uiState/UiStateStore'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import { useBrandImage, useWindowSize, ISiteLink } from 'common'
import { zIndex } from '../../../theme/zIndex'
import { Button } from '../../../ui-components'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { breakpoints } from '../../../theme/breakpoints'

enum Countries {
  US,
  CA,
  MEX,
}

function a11yProps(country: Countries) {
  return {
    'aria-controls': `country-tabpanel-${country}`,
    id: `country-tab-${country}`,
  }
}
interface ITabPanelProps {
  countrySiteLinks: ISiteLink[]
  value: Countries
  index: number
}
function TabPanel(props: ITabPanelProps) {
  const { countrySiteLinks, value, index } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`country-tabpanel-${index}`}
      aria-labelledby={`country-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <LinksContainer>
            {countrySiteLinks?.[index]?.siteLinks?.map((item) => (
              <LinksWrapper key={item.url}>
                <LinksTitle>{item?.name}</LinksTitle>
                <SelectButton
                  text="go"
                  onClick={() => {
                    window.open(item?.url, '_blank')
                  }}
                />
                <MobileChevron>
                  {!!item?.url && (
                    <a rel="noreferrer" href={item.url} target="_blank">
                      <ResponsiveChevronRight />
                    </a>
                  )}
                </MobileChevron>
              </LinksWrapper>
            ))}
          </LinksContainer>
        </Box>
      )}
    </div>
  )
}

export const BrandLinksDrawer = observer((): ReactElement => {
  const {
    showBrandLinksDrawer,
    setShowBrandLinksDrawer,
    brandLinksDrawerData,
  } = useUiStateStore()

  const [brandImgUrl, countrySiteLinks] = useBrandImage(brandLinksDrawerData)

  const handleClose = (): void => {
    setShowBrandLinksDrawer(false, null)
  }

  const [value, setValue] = useState<Countries>(Countries.US)

  const handleChange = (_event, newValue) => {
    setValue(newValue)
  }

  const countries = [
    { label: 'U.S.', index: Countries.US },
    { label: 'Canada', index: Countries.CA },
    { label: 'Latin America', index: Countries.MEX },
  ]
  const { width: windowWidth } = useWindowSize()
  const ref = useRef<HTMLInputElement>(null)
  return (
    <Drawer
      anchor={windowWidth < breakpoints.mobileLarge ? 'bottom' : 'right'}
      open={showBrandLinksDrawer}
      onClose={handleClose}
    >
      <Container ref={ref}>
        <UpperWrapper>
          <BrandTitleContainer>
            <BrandLogo src={brandImgUrl} />
            <BrandTitle> {brandLinksDrawerData?.brandName} </BrandTitle>
          </BrandTitleContainer>
          <StyledCloseIcon onClick={handleClose} />
        </UpperWrapper>
        <Box sx={{ width: '100%' }}>
          <CountriesTabsWrapper
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            {countries.map(
              (country) =>
                !!countrySiteLinks?.[country.index]?.siteLinks && (
                  <CountryTab
                    label={country.label}
                    {...a11yProps(country.index)}
                    key={country.index}
                  />
                )
            )}
          </CountriesTabsWrapper>

          <Divider />
          {countries.map((country) => (
            <TabPanel
              countrySiteLinks={countrySiteLinks}
              value={value}
              index={country.index}
              key={country.index}
            />
          ))}
        </Box>
      </Container>
    </Drawer>
  )
})

const ResponsiveChevronRight = styled(ChevronRightIcon)`
  color: #000000;
`

const MobileChevron = styled.div`
  display: none;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
  }
`

const Container = styled.div`
  height: 100%;
  width: 533px;
  background-color: ${defaultTheme.colors.white};
  z-index: ${zIndex.dropdown};
  /* padding: 2rem; */
  overflow-y: auto;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    width: 400px;
  }
`

const UpperWrapper = styled.div`
  margin: 2rem 2rem 0px 2rem;
  display: flex;
`

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
  position: absolute;
  right: 0;
  margin-right: 20px;
`

const BrandTitleContainer = styled.div`
  display: flex;
  gap: 12px;
  font-family: Oswald;
  font-weight: 800;
  line-height: 48px;
  letter-spacing: 0px;
  text-align: left;
`

const BrandLogo = styled.img`
  height: 44px;
  width: 64px;
`
const BrandTitle = styled.p`
  font-size: 28px;
`

const CountriesTabsWrapper = styled(Tabs)`
  display: flex;
  gap: 8px;
  align-items: baseline;
  margin: 0px 2rem 0px 2rem;
  div > span {
    background-color: ${defaultTheme.colors.primary};
  }
`

const CountryTab = styled(Tab)`
  font-family: Oswald;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
  padding: 0px;
  text-transform: capitalize;
  color: black !important;
  max-width: fit-content;
`

const LinksContainer = styled.div`
  width: 100%;
`

const LinksWrapper = styled.div`
  display: flex;
  padding: 2rem 0 2rem 0;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid silver;
  margin: 0px;
`

const LinksTitle = styled.div`
  /* text-align: left; */
  margin: 0px;
  padding: 0px;
  //styleName: H / 4;
  font-family: Oswald;
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
  letter-spacing: 0px;
  text-align: left;
`

const SelectButton = styled(Button)`
  min-width: 109.53px;
  margin: 0px;
  margin-left: 51%;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

const Divider = styled.div`
  margin-top: 14px;
  border-bottom: 1px solid ${defaultTheme.colors.border};
  width: 100%;
`
